import * as dashboard from './fragments/dashboard';
import * as inventory from './fragments/inventory';
import * as core from './fragments/core';
import * as linking from './fragments/linking';
import * as linkqual from './fragments/linkqual';
import * as scoring from './fragments/scoring';
import * as qualgap from './fragments/qualgap';
import * as portfolio from './fragments/portfolio';

export type Lang = 'fr' | 'en';

export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
];

type Dict = Record<string, string>;

// Base dictionary — app chrome, auth/admin kit labels, profile.
// Page-specific keys live in ./fragments/* and are merged in below.
const frBase: Dict = {
  // ── common ──
  'common.logout': 'Déconnexion',
  'common.loading': 'Chargement…',
  'common.cancel': 'Annuler',
  'common.save': 'Enregistrer',
  'common.saving': 'Enregistrement…',
  'common.add': 'Ajouter',
  'common.close': 'Fermer',
  'common.delete': 'Supprimer',
  'common.edit': 'Modifier',
  'common.search': 'Rechercher',
  'common.language': 'Langue',
  'common.confirm': 'Confirmer',
  'common.dashboard': 'Tableau de bord',
  'common.theme.switchToDark': 'Passer en mode sombre',
  'common.theme.switchToLight': 'Passer en mode clair',

  // ── topbar / CSV actions ──
  'csv.importing': 'Importation…',
  'csv.uploadCsv': 'Importer un CSV',
  'csv.exportCsv': 'Exporter en CSV',
  'csv.export': 'Exporter',

  // ── impersonation ──
  'impersonation.viewingAs': 'Consultation en tant que',
  'impersonation.by': 'imité par',
  'impersonation.stop': 'Arrêter l’imitation',

  // ── sidebar / nav ──
  'nav.inventory': 'Inventaire',
  'nav.rcAssessment': 'Évaluation RC',
  'nav.rcInsights': 'Analyses RC',
  'nav.collapse': 'Réduire',
  'nav.item.resources': 'Ressources',
  'nav.item.capabilities': 'Capacités',
  'nav.item.outcomes': 'Résultats',
  'nav.item.linking': 'Association RC',
  'nav.item.linkQualification': 'Qualification des associations RC',
  'nav.item.maturityScoring': 'Notation de maturité',
  'nav.item.weightScoring': 'Notation de pondération',
  'nav.item.weightMaturityQualification': 'Pondération et maturité',
  'nav.item.vrio': 'Notation VRIO',
  'nav.item.gapAnalysis': 'Analyse des écarts RC',

  // ── page titles (topbar) ──
  'page.dashboard': 'Tableau de bord',
  'page.resources': 'Ressources',
  'page.capabilities': 'Capacités',
  'page.outcomes': 'Résultats',
  'page.inventory-resources': 'Inventaire : Ressources',
  'page.inventory-capabilities': 'Inventaire : Capacités',
  'page.inventory-outcomes': 'Inventaire : Résultats',
  'page.linking': 'Association RC',
  'page.link-qualification': 'Qualification des associations RC',
  'page.maturity-scoring': 'Notation de maturité RC',
  'page.weight-scoring': 'Notation de pondération RC',
  'page.maturity-qualification': 'Qualification pondération et maturité RC',
  'page.vrio': 'Notation VRIO',
  'page.gap-analysis': 'Analyse des écarts RC',
  'page.admin': 'Contrôles d’administration',

  // ── login ──
  'login.title': 'RCAJO',
  'login.subtitle': 'Outil d’évaluation stratégique',
  'login.oidcButton': 'Se connecter avec Authentik',
  'login.emailPlaceholder': 'votre.nom@aeveil.ch',
  'login.emailLabel': 'E-mail',
  'login.passwordLabel': 'Mot de passe',
  'login.passwordPlaceholder': 'Saisissez votre mot de passe',
  'login.submit': 'Se connecter',
  'login.submitting': 'Connexion…',
  'login.footer.open': 'Inscription ouverte — le premier utilisateur devient administrateur',
  'login.footer.allowlist': 'Seules les adresses e-mail pré-approuvées peuvent se connecter',
  'login.err.emailRequired': 'L’adresse e-mail est requise',
  'login.err.passwordRequired': 'Le mot de passe est requis',
  'login.err.loginFailed': 'Échec de la connexion',
  'login.err.notAuthorized': 'Votre adresse e-mail n’est pas autorisée à accéder à cette application. Contactez un administrateur.',
  'login.err.oidcFailed': 'Échec de l’authentification. Veuillez réessayer.',
  'login.err.noEmail': 'Aucune adresse e-mail n’a été transmise par le fournisseur d’identité.',

  // ── profile menu ──
  'profile.title': 'Profil',
  'profile.menuAria': 'Ouvrir le menu du profil',

  // ── admin — user management (kit labels) ──
  'users.regMode.title': 'Mode d’inscription',
  'users.regMode.open': 'Ouvert',
  'users.regMode.allowlist': 'Liste d’accès',
  'users.regMode.openHelp': 'Toute personne avec une adresse e-mail valide peut s’inscrire. Le premier utilisateur devient administrateur.',
  'users.regMode.allowlistHelp': 'Seules les adresses pré-approuvées peuvent se connecter.',
  'users.allowlist.title': 'Liste des e-mails autorisés',
  'users.allowlist.notEnforced': 'La liste n’est pas appliquée actuellement. Passez en mode Liste d’accès pour restreindre l’inscription.',
  'users.allowlist.helpEnforced': 'Seules ces adresses e-mail peuvent se connecter. Ajoutez les adresses avant la première authentification.',
  'users.allowlist.helpNotEnforced': 'Gérez les adresses en vue du passage en mode Liste d’accès.',
  'users.emailPlaceholder': 'email@exemple.ch',
  'users.namePlaceholder': 'Nom (optionnel)',
  'users.addToAllowlist': 'Ajouter à la liste',
  'users.createAccount': 'Créer le compte',
  'users.errValidEmail': 'Adresse e-mail valide requise',
  'users.col.email': 'E-mail',
  'users.col.status': 'Statut',
  'users.col.added': 'Ajouté le',
  'users.status.registered': 'Inscrit ({name})',
  'users.status.pending': 'En attente',
  'users.impersonate': 'Imiter',
  'users.remove': 'Retirer',
  'users.confirmRemoveEmail': 'Retirer « {email} » de la liste ?',
  'users.mgmt.title': 'Gestion des utilisateurs',
  'users.col.user': 'Utilisateur',
  'users.col.role': 'Rôle',
  'users.col.joined': 'Inscrit le',
  'users.badge.admin': 'Admin',
  'users.badge.user': 'Utilisateur',
  'users.removeAdmin': 'Retirer admin',
  'users.makeAdmin': 'Rendre admin',
  'users.roles': 'Rôles',
  'users.resetPassword': 'Réinitialiser le mot de passe',
  'users.delete': 'Supprimer',
  'users.confirmDelete': 'Supprimer l’utilisateur « {name} » ? Toutes ses données seront également supprimées.',
  'users.newPasswordPlaceholder': 'Nouveau mot de passe (min. 8 caractères)',
  'users.set': 'Définir',
  'users.saveRoles': 'Enregistrer les rôles',
  'users.errPasswordMin': 'Le mot de passe doit contenir au moins 8 caractères',
  'users.passwordResetDone': 'Mot de passe réinitialisé',

  // ── admin — audit log (kit labels) ──
  'audit.allActions': 'Toutes les actions',
  'audit.resourceType': 'Type de ressource',
  'audit.startDate': 'Date de début',
  'audit.endDate': 'Date de fin',
  'audit.searchPlaceholder': 'Rechercher…',
  'audit.search': 'Rechercher',
  'audit.clear': 'Effacer',
  'audit.col.time': 'Heure',
  'audit.col.user': 'Utilisateur',
  'audit.col.action': 'Action',
  'audit.col.resourceType': 'Type de ressource',
  'audit.col.resourceId': 'ID ressource',
  'audit.col.details': 'Détails',
  'audit.col.ip': 'IP',
  'audit.loading': 'Chargement…',
  'audit.empty': 'Aucune entrée de journal trouvée',
  'audit.loadFailed': 'Échec du chargement du journal d’audit',
  'audit.clickToExpand': 'Cliquer pour développer',
  'audit.totalEntries': '{n} entrées au total',
  'audit.pageOf': 'Page {page} sur {pages}',
  'audit.prev': 'Précédent',
  'audit.next': 'Suivant',

  // ── admin — notification preferences (kit labels) ──
  'notif.title': 'Préférences de notification',
  'notif.loading': 'Chargement des préférences…',
  'notif.sponsorBadge': 'Sponsor',
  'notif.all': 'Toutes les notifications',
  'notif.sponsoredOnly': 'Sponsorisées uniquement',

  // ── admin page (RCAJO-specific) ──
  'admin.badge': 'Admin',
  'admin.devReset.title': 'Données de dev — réinitialiser depuis la production',
  'admin.devReset.help': 'Efface cette base de données de dev et la remplace par une copie actuelle de la production, puis réapplique les migrations de dev. La production est en lecture seule et n’est jamais modifiée. Ce contrôle n’existe que sur dev.',
  'admin.devReset.button': 'Réinitialiser dev depuis prod',
  'admin.devReset.last': 'Dernière réinitialisation :',
  'admin.devReset.confirmPrompt': 'Saisissez RESET pour confirmer l’effacement de dev et la copie de la production actuelle :',
  'admin.devReset.resetting': 'Réinitialisation…',
  'admin.devReset.confirm': 'Confirmer la réinitialisation',
  'admin.devReset.done': 'Base de données de dev réinitialisée depuis la prod. Rechargement…',
  'admin.devReset.failed': 'Échec de la réinitialisation',
  'admin.toggles.title': 'Bascules des phases d’évaluation',
  'admin.toggles.help': 'Contrôlez quels onglets d’évaluation sont accessibles aux utilisateurs. Activez les phases séquentiellement au fur et à mesure de l’avancement de l’évaluation.',
  'admin.toggles.linking.title': '1. Association ressources–capacités',
  'admin.toggles.linking.help': 'Les utilisateurs peuvent accéder à l’onglet Association RC et commencer à associer les ressources aux capacités.',
  'admin.toggles.linkQual.title': '2. Qualification des associations RC',
  'admin.toggles.linkQual.help': 'Déverrouille l’onglet Qualification des associations RC (lecture seule pour les utilisateurs). Active aussi la vue agrégée dans Association RC.',
  'admin.toggles.scoring.title': '3. Notation de maturité et de pondération RC',
  'admin.toggles.scoring.help': 'Déverrouille les onglets Notation de maturité et Notation de pondération.',
  'admin.toggles.weightMatQual.title': '4. Qualification pondération et maturité RC',
  'admin.toggles.weightMatQual.help': 'Déverrouille l’onglet Qualification pondération et maturité (lecture seule pour les utilisateurs).',
  'admin.toggles.vrio.title': '5. Notation VRIO',
  'admin.toggles.vrio.help': 'Déverrouille l’onglet Notation VRIO pour que les utilisateurs évaluent les ressources présélectionnées selon leurs propres dimensions VRIO.',
  'admin.accent.title': 'Couleur d’accentuation',
  'admin.accent.help': 'Couleur de marque utilisée pour les boutons, les liens et les états actifs.',
  'admin.accent.custom': 'Couleur personnalisée',
  'admin.accent.color.indigo': 'Indigo',
  'admin.accent.color.teal': 'Sarcelle',
  'admin.accent.color.violet': 'Violet',
  'admin.accent.color.rose': 'Rose',
  'admin.accent.color.slate': 'Ardoise',
  'admin.logo.title': 'Logo',
  'admin.logo.help': 'Téléversez un PNG à fond transparent. S’affiche dans la barre d’en-tête à côté du nom de la marque. S’inverse automatiquement en mode sombre.',
  'admin.logo.current': 'Logo actuel',
  'admin.logo.replace': 'Remplacer',
  'admin.logo.remove': 'Supprimer',
  'admin.logo.upload': 'Téléverser un logo',
  'admin.logo.errPng': 'Seuls les fichiers PNG sont acceptés',
  'admin.logo.errSize': 'Fichier trop volumineux (max 1,5 Mo)',
  'admin.users.col.links': 'Associations',
  'admin.users.col.ratings': 'Évaluations',
  'admin.users.col.vrio': 'VRIO',
};

const enBase: Dict = {
  // ── common ──
  'common.logout': 'Logout',
  'common.loading': 'Loading…',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.add': 'Add',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.search': 'Search',
  'common.language': 'Language',
  'common.confirm': 'Confirm',
  'common.dashboard': 'Dashboard',
  'common.theme.switchToDark': 'Switch to dark mode',
  'common.theme.switchToLight': 'Switch to light mode',

  // ── topbar / CSV actions ──
  'csv.importing': 'Importing...',
  'csv.uploadCsv': 'Upload CSV',
  'csv.exportCsv': 'Export CSV',
  'csv.export': 'Export',

  // ── impersonation ──
  'impersonation.viewingAs': 'Viewing as',
  'impersonation.by': 'impersonated by',
  'impersonation.stop': 'Stop Impersonating',

  // ── sidebar / nav ──
  'nav.inventory': 'Inventory',
  'nav.rcAssessment': 'RC Assessment',
  'nav.rcInsights': 'RC Insights',
  'nav.collapse': 'Collapse',
  'nav.item.resources': 'Resources',
  'nav.item.capabilities': 'Capabilities',
  'nav.item.outcomes': 'Outcomes',
  'nav.item.linking': 'Linking RC',
  'nav.item.linkQualification': 'RC Link Qualification',
  'nav.item.maturityScoring': 'Maturity Scoring',
  'nav.item.weightScoring': 'Weight Scoring',
  'nav.item.weightMaturityQualification': 'Weight & Maturity Qualification',
  'nav.item.vrio': 'VRIO Scoring',
  'nav.item.gapAnalysis': 'RC Gap Analysis',

  // ── page titles (topbar) ──
  'page.dashboard': 'Dashboard',
  'page.resources': 'Resources',
  'page.capabilities': 'Capabilities',
  'page.outcomes': 'Outcomes',
  'page.inventory-resources': 'Inventory: Resources',
  'page.inventory-capabilities': 'Inventory: Capabilities',
  'page.inventory-outcomes': 'Inventory: Outcomes',
  'page.linking': 'Linking RC',
  'page.link-qualification': 'RC Link Qualification',
  'page.maturity-scoring': 'RC Maturity Scoring',
  'page.weight-scoring': 'RC Weight Scoring',
  'page.maturity-qualification': 'RC Weight & Maturity Qualification',
  'page.vrio': 'VRIO Scoring',
  'page.gap-analysis': 'RC Gap Analysis',
  'page.admin': 'Admin Controls',

  // ── login ──
  'login.title': 'RCAJO',
  'login.subtitle': 'Strategic Assessment Tool',
  'login.oidcButton': 'Log in with Authentik',
  'login.emailPlaceholder': 'your.name@aeveil.ch',
  'login.emailLabel': 'Email',
  'login.passwordLabel': 'Password',
  'login.passwordPlaceholder': 'Enter your password',
  'login.submit': 'Log in',
  'login.submitting': 'Logging in…',
  'login.footer.open': 'Open registration — first user becomes admin',
  'login.footer.allowlist': 'Only pre-approved email addresses can log in',
  'login.err.emailRequired': 'Email is required',
  'login.err.passwordRequired': 'Password is required',
  'login.err.loginFailed': 'Login failed',
  'login.err.notAuthorized': 'Your email is not authorized to access this application. Contact an administrator.',
  'login.err.oidcFailed': 'Authentication failed. Please try again.',
  'login.err.noEmail': 'No email was received from the identity provider.',

  // ── profile menu ──
  'profile.title': 'Profile',
  'profile.menuAria': 'Open profile menu',

  // ── admin — user management (kit labels) ──
  'users.regMode.title': 'Registration mode',
  'users.regMode.open': 'Open',
  'users.regMode.allowlist': 'Allowlist',
  'users.regMode.openHelp': 'Anyone with a valid email can register. The first user becomes admin.',
  'users.regMode.allowlistHelp': 'Only pre-approved emails can log in.',
  'users.allowlist.title': 'Email allowlist',
  'users.allowlist.notEnforced': 'The allowlist is not currently enforced. Switch to Allowlist mode to restrict registration.',
  'users.allowlist.helpEnforced': 'Only these email addresses can log in. Add emails before users authenticate.',
  'users.allowlist.helpNotEnforced': 'Manage emails for when you switch to Allowlist mode.',
  'users.emailPlaceholder': 'email@example.com',
  'users.namePlaceholder': 'Name (optional)',
  'users.addToAllowlist': 'Add to allowlist',
  'users.createAccount': 'Create account',
  'users.errValidEmail': 'Valid email is required',
  'users.col.email': 'Email',
  'users.col.status': 'Status',
  'users.col.added': 'Added',
  'users.status.registered': 'Registered ({name})',
  'users.status.pending': 'Pending',
  'users.impersonate': 'Impersonate',
  'users.remove': 'Remove',
  'users.confirmRemoveEmail': 'Remove "{email}" from the allowlist?',
  'users.mgmt.title': 'User management',
  'users.col.user': 'User',
  'users.col.role': 'Role',
  'users.col.joined': 'Joined',
  'users.badge.admin': 'Admin',
  'users.badge.user': 'User',
  'users.removeAdmin': 'Remove admin',
  'users.makeAdmin': 'Make admin',
  'users.roles': 'Roles',
  'users.resetPassword': 'Reset password',
  'users.delete': 'Delete',
  'users.confirmDelete': 'Delete user "{name}"? This will also delete all their data.',
  'users.newPasswordPlaceholder': 'New password (min 8 chars)',
  'users.set': 'Set',
  'users.saveRoles': 'Save roles',
  'users.errPasswordMin': 'Password must be at least 8 characters',
  'users.passwordResetDone': 'Password reset successfully',

  // ── admin — audit log (kit labels) ──
  'audit.allActions': 'All actions',
  'audit.resourceType': 'Resource type',
  'audit.startDate': 'Start date',
  'audit.endDate': 'End date',
  'audit.searchPlaceholder': 'Search…',
  'audit.search': 'Search',
  'audit.clear': 'Clear',
  'audit.col.time': 'Time',
  'audit.col.user': 'User',
  'audit.col.action': 'Action',
  'audit.col.resourceType': 'Resource type',
  'audit.col.resourceId': 'Resource ID',
  'audit.col.details': 'Details',
  'audit.col.ip': 'IP',
  'audit.loading': 'Loading…',
  'audit.empty': 'No audit log entries found',
  'audit.loadFailed': 'Failed to load audit log',
  'audit.clickToExpand': 'Click to expand',
  'audit.totalEntries': '{n} total entries',
  'audit.pageOf': 'Page {page} of {pages}',
  'audit.prev': 'Previous',
  'audit.next': 'Next',

  // ── admin — notification preferences (kit labels) ──
  'notif.title': 'Notification preferences',
  'notif.loading': 'Loading preferences…',
  'notif.sponsorBadge': 'Sponsor',
  'notif.all': 'All notifications',
  'notif.sponsoredOnly': 'Sponsored only',

  // ── admin page (RCAJO-specific) ──
  'admin.badge': 'Admin',
  'admin.devReset.title': 'Dev Data — Reset from Production',
  'admin.devReset.help': 'Wipe this dev database and replace it with a current copy of production, then re-apply dev migrations. Production is read-only and is never modified. This control only exists on dev.',
  'admin.devReset.button': 'Reset dev from prod',
  'admin.devReset.last': 'Last reset:',
  'admin.devReset.confirmPrompt': 'Type RESET to confirm wiping dev and copying current production:',
  'admin.devReset.resetting': 'Resetting…',
  'admin.devReset.confirm': 'Confirm reset',
  'admin.devReset.done': 'Dev database reset from prod. Reloading…',
  'admin.devReset.failed': 'Refresh failed',
  'admin.toggles.title': 'Assessment Phase Toggles',
  'admin.toggles.help': 'Control which assessment tabs are accessible to users. Enable phases sequentially as the assessment progresses.',
  'admin.toggles.linking.title': '1. Resource-Capability Linking',
  'admin.toggles.linking.help': 'Users can access the Linking RC tab and start linking resources to capabilities.',
  'admin.toggles.linkQual.title': '2. RC Link Qualification',
  'admin.toggles.linkQual.help': 'Unlocks the RC Link Qualification tab (read-only for users). Also enables the aggregate view in Linking RC.',
  'admin.toggles.scoring.title': '3. RC Maturity and Weight Scoring',
  'admin.toggles.scoring.help': 'Unlocks the Maturity Scoring and Weight Scoring tabs.',
  'admin.toggles.weightMatQual.title': '4. RC Weight & Maturity Qualification',
  'admin.toggles.weightMatQual.help': 'Unlocks the Weight & Maturity Qualification tab (read-only for users).',
  'admin.toggles.vrio.title': '5. VRIO Scoring',
  'admin.toggles.vrio.help': 'Unlocks the VRIO Scoring tab so users can rate shortlisted resources on their own VRIO dimensions.',
  'admin.accent.title': 'Accent Color',
  'admin.accent.help': 'Brand color used for buttons, links, and active states.',
  'admin.accent.custom': 'Custom color',
  'admin.accent.color.indigo': 'Indigo',
  'admin.accent.color.teal': 'Teal',
  'admin.accent.color.violet': 'Violet',
  'admin.accent.color.rose': 'Rose',
  'admin.accent.color.slate': 'Slate',
  'admin.logo.title': 'Logo',
  'admin.logo.help': 'Upload a PNG with transparent background. Displays in the topbar next to the brand name. Inverts automatically in dark mode.',
  'admin.logo.current': 'Current logo',
  'admin.logo.replace': 'Replace',
  'admin.logo.remove': 'Remove',
  'admin.logo.upload': 'Upload Logo',
  'admin.logo.errPng': 'Only PNG files are accepted',
  'admin.logo.errSize': 'File too large (max 1.5MB)',
  'admin.users.col.links': 'Links',
  'admin.users.col.ratings': 'Ratings',
  'admin.users.col.vrio': 'VRIO',
};

const fr: Dict = {
  ...frBase,
  ...dashboard.fr,
  ...inventory.fr,
  ...core.fr,
  ...linking.fr,
  ...linkqual.fr,
  ...scoring.fr,
  ...qualgap.fr,
  ...portfolio.fr,
};

const en: Dict = {
  ...enBase,
  ...dashboard.en,
  ...inventory.en,
  ...core.en,
  ...linking.en,
  ...linkqual.en,
  ...scoring.en,
  ...qualgap.en,
  ...portfolio.en,
};

export const dictionaries: Record<Lang, Dict> = { fr, en };
